import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Api, ApiError, type Me, type SignupMode } from "../api.js";
import { TextInput } from "../components/ui.js";
import { useI18n } from "../i18n.js";

/** The sentence to show for a failed call: the server's own plain-language message, or our network message. */
export function errorText(e: unknown, fallback: string): string {
  return e instanceof ApiError ? e.message : fallback;
}

export function LangToggle() {
  const { lang, setLang, t } = useI18n();
  return (
    <div className="lang-toggle" role="group" aria-label={t("lang.label")}>
      {(["en", "es"] as const).map((l) => (
        <button key={l} type="button" className={`btn ghost small ${lang === l ? "active" : ""}`} aria-pressed={lang === l} lang={l} onClick={() => setLang(l)}>
          {t(`lang.${l}`)}
        </button>
      ))}
    </div>
  );
}

export function AuthShell({ title, lead, children, footer }: { title: string; lead?: string; children: ReactNode; footer?: ReactNode }) {
  return (
    <main className="login" id="main">
      <div className="card auth-card">
        <div className="row between">
          <p className="brand-mark">Nemo</p>
          <LangToggle />
        </div>
        <h1>{title}</h1>
        {lead && <p>{lead}</p>}
        {children}
        {footer && <p className="auth-footer">{footer}</p>}
      </div>
    </main>
  );
}

const go = (hash: string) => () => {
  location.hash = hash;
};

export function LoginScreen({ api, google, notice, onSignedIn, onApiKey, canSignUp = true }: { api: Api; google: boolean; notice?: string | null; onSignedIn: (me: Me) => void; onApiKey?: () => void; canSignUp?: boolean }) {
  const { t, lang } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resent, setResent] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNeedsVerify(false);
    try {
      onSignedIn(await api.login(email.trim(), password, lang));
    } catch (err) {
      setError(errorText(err, t("common.network")));
      setNeedsVerify(err instanceof ApiError && err.code === "email_not_verified");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title={t("login.title")}
      footer={
        canSignUp ? (
          <>
            {t("login.noAccount")} <a href="#/signup">{t("login.create")}</a>
          </>
        ) : undefined
      }
    >
      {notice && <div className="banner warn" role="alert">{notice}</div>}
      {google && (
        <>
          <a className="btn secondary wide" href={api.googleStartUrl(lang)}>
            {t("login.google")}
          </a>
          <p className="divider" aria-hidden="true">{t("common.or")}</p>
        </>
      )}
      <form onSubmit={submit} noValidate>
        <TextInput label={t("common.email")} type="email" autoComplete="username" value={email} onChange={setEmail} required />
        <TextInput label={t("common.password")} type="password" autoComplete="current-password" value={password} onChange={setPassword} required error={error ?? undefined} />
        {needsVerify && (
          <p>
            {resent ? (
              <span role="status">{t("login.resent")}</span>
            ) : (
              <button type="button" className="link" onClick={() => void api.resendVerification(email.trim(), lang).then(() => setResent(true), () => setResent(true))}>
                {t("login.resend")}
              </button>
            )}
          </p>
        )}
        <button className="btn primary wide" type="submit" disabled={busy || !email.trim() || !password}>
          {busy ? t("login.busy") : t("login.submit")}
        </button>
      </form>
      <p>
        <a href="#/forgot">{t("login.forgot")}</a>
      </p>
      {onApiKey && (
        <p className="hint">
          <button type="button" className="link" onClick={onApiKey}>
            {t("login.apiKey")}
          </button>
        </p>
      )}
    </AuthShell>
  );
}

export function SignupScreen({ api, google, mode = "open", domains = [], invite, onDone, onSignedIn }: { api: Api; google: boolean; mode?: SignupMode; domains?: string[]; invite?: string; onDone: (email: string) => void; onSignedIn?: (me: Me) => void }) {
  const { t, lang } = useI18n();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  // Invitation-only: the address on the invitation is the only one that can sign up with it.
  const [invitedEmail, setInvitedEmail] = useState<string | null>(null);
  const [inviteState, setInviteState] = useState<"none" | "checking" | "ok" | "invalid">(invite && mode === "invite" ? "checking" : "none");
  useEffect(() => {
    if (!invite || mode !== "invite") return;
    let live = true;
    api.signupInvitation(invite, lang).then(
      (r) => {
        if (!live) return;
        setInvitedEmail(r.email);
        setEmail(r.email);
        setInviteState("ok");
      },
      () => live && setInviteState("invalid"),
    );
    return () => {
      live = false;
    };
  }, [api, invite, mode, lang]);
  const [password, setPassword] = useState("");
  const [business, setBusiness] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ field: "email" | "password" | "name" | "form"; message: string } | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.signup({ email: email.trim(), password, name: name.trim(), language: lang, accountName: business.trim() || undefined, inviteToken: inviteState === "ok" ? invite : undefined });
      // An invited person is signed in at once (the invitation proved their address).
      if (r.user && onSignedIn) onSignedIn(r as Me);
      else onDone(email.trim());
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "";
      setError({ field: code === "invalid_email" ? "email" : code === "weak_password" ? "password" : code === "invalid_name" ? "name" : "form", message: errorText(err, t("common.network")) });
    } finally {
      setBusy(false);
    }
  };

  const openToDomains = mode === "invite" && !invite && domains.length > 0;
  if (mode === "closed" || (mode === "invite" && !openToDomains && (!invite || inviteState === "invalid"))) {
    const closed = mode === "closed";
    return (
      <AuthShell title={closed ? t("signup.closedTitle") : inviteState === "invalid" ? t("signup.invalidInvite") : t("signup.inviteOnlyTitle")} footer={<>{t("signup.haveAccount")} <a href="#/login">{t("login.submit")}</a></>}>
        <p role="status">{closed ? t("signup.closedBody") : t("signup.inviteOnlyBody")}</p>
      </AuthShell>
    );
  }
  if (inviteState === "checking") return <AuthShell title={t("signup.title")}><p role="status">{t("common.loading")}</p></AuthShell>;

  return (
    <AuthShell
      title={t("signup.title")}
      lead={inviteState === "ok" ? t("signup.invitedLead", { email: invitedEmail ?? "" }) : openToDomains ? t("signup.domainsLead", { domains: domains.map((d) => `@${d}`).join(lang === "es" ? " o " : " or ") }) : t("signup.lead")}
      footer={
        <>
          {t("signup.haveAccount")} <a href="#/login">{t("login.submit")}</a>
        </>
      }
    >
      {google && (
        <>
          <a className="btn secondary wide" href={api.googleStartUrl(lang, inviteState === "ok" ? invite : undefined)}>
            {t("login.google")}
          </a>
          <p className="divider" aria-hidden="true">{t("common.or")}</p>
        </>
      )}
      <form onSubmit={submit} noValidate>
        <TextInput label={t("common.name")} autoComplete="name" value={name} onChange={setName} required error={error?.field === "name" ? error.message : undefined} />
        <TextInput label={t("common.email")} type="email" autoComplete="email" value={email} onChange={invitedEmail ? () => undefined : setEmail} required help={invitedEmail ? t("signup.invitedEmailHelp") : undefined} error={error?.field === "email" ? error.message : undefined} />
        <TextInput label={t("common.password")} type="password" autoComplete="new-password" value={password} onChange={setPassword} required help={t("signup.passwordHelp")} error={error?.field === "password" ? error.message : undefined} />
        <TextInput label={t("signup.business")} autoComplete="organization" value={business} onChange={setBusiness} help={t("signup.businessHelp")} />
        {error?.field === "form" && <div className="banner warn" role="alert">{error.message}</div>}
        <button className="btn primary wide" type="submit" disabled={busy || !name.trim() || !email.trim() || !password}>
          {busy ? t("signup.busy") : t("signup.submit")}
        </button>
      </form>
      <p className="hint">{t("signup.terms")}</p>
    </AuthShell>
  );
}

export function CheckEmailScreen({ api, email }: { api: Api; email: string }) {
  const { t, lang } = useI18n();
  const [again, setAgain] = useState(false);
  return (
    <AuthShell title={t("check.title")}>
      <p role="status">{t("check.body", { email })}</p>
      <p className="hint">
        {t("check.spam")}{" "}
        {again ? (
          <span role="status">{t("login.resent")}</span>
        ) : (
          <button type="button" className="link" onClick={() => void api.resendVerification(email, lang).then(() => setAgain(true), () => setAgain(true))}>
            {t("check.again")}
          </button>
        )}
      </p>
      <p>
        <a href="#/login">{t("common.back")}</a>
      </p>
    </AuthShell>
  );
}

export function ForgotScreen({ api }: { api: Api }) {
  const { t, lang } = useI18n();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.forgotPassword(email.trim(), lang);
      setDone(true);
    } catch (err) {
      setError(errorText(err, t("common.network")));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell title={t("forgot.title")} lead={done ? undefined : t("forgot.lead")} footer={<a href="#/login">{t("common.back")}</a>}>
      {done ? (
        <p role="status">{t("forgot.done")}</p>
      ) : (
        <form onSubmit={submit} noValidate>
          <TextInput label={t("common.email")} type="email" autoComplete="email" value={email} onChange={setEmail} required error={error ?? undefined} />
          <button className="btn primary wide" type="submit" disabled={busy || !email.trim()}>
            {t("forgot.submit")}
          </button>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetScreen({ api, token }: { api: Api; token: string }) {
  const { t, lang } = useI18n();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.resetPassword(token, password, lang);
      setDone(true);
    } catch (err) {
      setError(errorText(err, t("common.network")));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell title={t("reset.title")}>
      {done ? (
        <>
          <p role="status">{t("reset.done")}</p>
          <button className="btn primary wide" onClick={go("#/login")}>
            {t("login.submit")}
          </button>
        </>
      ) : (
        <form onSubmit={submit} noValidate>
          <TextInput label={t("account.newPassword")} type="password" autoComplete="new-password" value={password} onChange={setPassword} required help={t("signup.passwordHelp")} error={error ?? undefined} />
          <button className="btn primary wide" type="submit" disabled={busy || !password}>
            {t("reset.submit")}
          </button>
        </form>
      )}
    </AuthShell>
  );
}

/** Opened from the confirmation e-mail: the link signs the person in. */
export function VerifyScreen({ api, token, onSignedIn }: { api: Api; token: string; onSignedIn: (me: Me) => void }) {
  const { t } = useI18n();
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    api.verifyEmail(token).then(
      (me) => live && onSignedIn(me),
      () => live && setFailed(true),
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, token]);
  return (
    <AuthShell title={t("brand")}>
      {failed ? (
        <>
          <div className="banner warn" role="alert">{t("verify.failed")}</div>
          <a className="btn primary wide" href="#/login">
            {t("login.submit")}
          </a>
        </>
      ) : (
        <p role="status">{t("verify.working")}</p>
      )}
    </AuthShell>
  );
}

export function AcceptInviteScreen({ api, token, me, onJoined }: { api: Api; token: string; me: Me | null; onJoined: (me: Me) => void }) {
  const { t, lang } = useI18n();
  const [info, setInfo] = useState<{ accountName: string; email: string; role: "owner" | "manager" | "member"; hasLogin: boolean } | null>(null);
  const [invalid, setInvalid] = useState(false);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.lookupInvite(token).then(setInfo, () => setInvalid(true));
  }, [api, token]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onJoined(await api.acceptInvite({ token, name: name.trim(), password, language: lang }));
    } catch (err) {
      setError(errorText(err, t("common.network")));
    } finally {
      setBusy(false);
    }
  };

  if (invalid) return <AuthShell title={t("invite.title")}><div className="banner warn" role="alert">{t("invite.invalid")}</div></AuthShell>;
  if (!info) return <AuthShell title={t("invite.title")}><p>{t("common.loading")}</p></AuthShell>;
  const signedInAsInvited = me?.user.email.toLowerCase() === info.email.toLowerCase();
  return (
    <AuthShell title={t("invite.title")} lead={t("invite.body", { account: info.accountName, role: t(`role.${info.role}`).toLowerCase() })}>
      {info.hasLogin && !signedInAsInvited && (
        <>
          <p>{t("invite.signInFirst", { email: info.email })}</p>
          <a className="btn primary wide" href="#/login">
            {t("login.submit")}
          </a>
        </>
      )}
      {info.hasLogin && signedInAsInvited && (
        <>
          {error && <div className="banner warn" role="alert">{error}</div>}
          <button className="btn primary wide" disabled={busy} onClick={(e) => void submit(e)}>
            {t("invite.accept")}
          </button>
        </>
      )}
      {!info.hasLogin && (
        <form onSubmit={submit} noValidate>
          <p>{t("invite.newPerson", { email: info.email })}</p>
          <TextInput label={t("common.name")} autoComplete="name" value={name} onChange={setName} required />
          <TextInput label={t("common.password")} type="password" autoComplete="new-password" value={password} onChange={setPassword} required help={t("signup.passwordHelp")} error={error ?? undefined} />
          <button className="btn primary wide" type="submit" disabled={busy || !name.trim() || !password}>
            {t("invite.accept")}
          </button>
        </form>
      )}
    </AuthShell>
  );
}

/** Opened from the deletion e-mails: ask for a copy of the data. No sign-in (the account is gone); the link is the proof. */
export function ExportRequestScreen({ api, token }: { api: Api; token: string }) {
  const { t, lang } = useI18n();
  const [info, setInfo] = useState<{ accountName: string; purgeAfter: string; requested: boolean } | null>(null);
  const [invalid, setInvalid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.exportStatus(token).then(setInfo, () => setInvalid(true));
  }, [api, token]);
  const ask = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.exportRequest(token);
      setSent(true);
    } catch (e) {
      setError(e instanceof ApiError && e.status === 429 ? t("export.tooMany") : t("export.invalid"));
    } finally {
      setBusy(false);
    }
  };
  if (invalid) return <AuthShell title={t("export.title")}><div className="banner warn" role="alert">{t("export.invalid")}</div></AuthShell>;
  if (!info) return <AuthShell title={t("export.title")}><p>{t("common.loading")}</p></AuthShell>;
  return (
    <AuthShell title={t("export.title")} lead={t("export.lead", { account: info.accountName, date: new Date(info.purgeAfter).toLocaleDateString(lang, { day: "numeric", month: "long", year: "numeric" }) })}>
      <p>{t("export.what")}</p>
      {error && <div className="banner warn" role="alert">{error}</div>}
      {sent ? (
        <p role="status">{t("export.sent")}</p>
      ) : (
        <button className="btn primary wide" onClick={() => void ask()} disabled={busy}>
          {busy ? t("export.sending") : t("export.button")}
        </button>
      )}
    </AuthShell>
  );
}

/** The link in the "your data is ready" e-mail. */
export function DownloadExportScreen({ api, token }: { api: Api; token: string }) {
  const { t } = useI18n();
  const [error, setError] = useState<string | null>(null);
  const get = async (file: "json" | "csv") => {
    setError(null);
    try {
      const text = await api.exportDownload(token, file);
      const url = URL.createObjectURL(new Blob([text], { type: file === "csv" ? "text/csv;charset=utf-8" : "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = file === "csv" ? "nemo-leads.csv" : "nemo-data.json";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      setError(t("download.invalid"));
    }
  };
  return (
    <AuthShell title={t("download.title")} lead={t("download.lead")}>
      {error && <div className="banner warn" role="alert">{error}</div>}
      <button className="btn primary wide" onClick={() => void get("json")}>
        {t("download.json")}
      </button>
      <button className="btn secondary wide" onClick={() => void get("csv")}>
        {t("download.csv")}
      </button>
    </AuthShell>
  );
}
